import { RPUser, RPUserPermission } from '@hazadapt-git/public-core-base'
import { FC, useRef } from 'react'
import { Checkbox, Popover } from '../atoms'
import Grid from '@mui/material/Grid'
import { Field, useFormikContext } from 'formik'
import Divider from '@mui/material/Divider'
import { makeStyles } from 'tss-react/mui'
import { isUserAdmin } from '../../lib/utils'
import Alert from '@mui/material/Alert'
import { AdminIcon } from '../molecules'

interface PermissionsListProps {
    selectedPermissions: RPUserPermission[]
    activeUser: RPUser
}

// OWNER and ADMIN are controlled on their own and not in PermissionsList
export const permissionsMap: Map<RPUserPermission, string> = new Map<
    RPUserPermission,
    string
>([
    [RPUserPermission.ADD_MEMBER, 'Add Member to Organization'],
    [RPUserPermission.EDIT_MEMBER, 'Edit Team Member'],
    [RPUserPermission.DELETE_MEMBER, 'Delete Member from Organization'],
    // [RPUserPermission.EDIT_ORGANIZATION, 'Edit Organization'], // This is now controlled by Admin
    // [RPUserPermission.MANAGE_BILLING, 'Manage Billing'], // This is now controlled by Admin
    // [RPUserPermission.PAUSE_MEMBER_ACCESS, 'Pause Member Access'], // This is not yet implemented
])

export const PermissionsList: FC<PermissionsListProps> = (
    props: PermissionsListProps
) => {
    const { classes: localClasses } = useLocalStyles()
    const allPermissions = Array.from(permissionsMap.keys())
    const { setFieldValue } = useFormikContext()
    const initiallyAdmin = useRef(
        props.selectedPermissions.includes(RPUserPermission.ADMIN)
    )
    const activeUserIsAdmin = isUserAdmin(props.activeUser)
    const adminChecked = props.selectedPermissions.includes(
        RPUserPermission.ADMIN
    )

    const adminClick = () => {
        let newPermissions = [...props.selectedPermissions]
        if (!props.selectedPermissions.includes(RPUserPermission.ADMIN)) {
            newPermissions = [
                ...newPermissions,
                ...allPermissions.filter((p) => !newPermissions.includes(p)),
                RPUserPermission.ADMIN,
            ]
        } else {
            newPermissions = newPermissions.filter(
                (p) => p !== RPUserPermission.ADMIN
            )
        }
        setFieldValue('permissions', newPermissions)
    }

    const handlePermissionChange =
        (permission: RPUserPermission) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            let newPermissions = [...props.selectedPermissions]
            if (event.target.checked) {
                newPermissions.push(permission)
            } else {
                newPermissions = newPermissions.filter((p) => p !== permission)
                if (
                    props.selectedPermissions.includes(RPUserPermission.ADMIN)
                ) {
                    newPermissions = newPermissions.filter(
                        (p) => p !== RPUserPermission.ADMIN
                    )
                }
            }
            setFieldValue('permissions', newPermissions)
        }

    return (
        <Grid container rowSpacing={2}>
            <Grid item xs={12}>
                <label className={localClasses.checkboxWrapper}>
                    <Field
                        as={Checkbox}
                        type="checkbox"
                        name="permissions"
                        value={RPUserPermission.ADMIN}
                        disabled={!activeUserIsAdmin}
                        checked={adminChecked}
                        onChange={adminClick}
                    />
                    <AdminIcon
                        showType
                        labelClassName={localClasses.adminLabel}
                    />
                    <Popover
                        helpText={
                            activeUserIsAdmin
                                ? 'Administrators are granted all permissions listed below, as well as the ability to edit organization details and manage subscriptions and payment methods.'
                                : 'Only Administrators are able to grant or revoke the Administrator permission.'
                        }
                    />
                </label>
                <Divider
                    sx={{
                        margin: '0.5rem 0',
                        width: '100%',
                    }}
                />
            </Grid>

            {/* Fake permissions checkboxes to show what controls all users have */}
            <Grid item xs={12} sm={6} lg={4}>
                <label className={localClasses.checkboxWrapper}>
                    <Checkbox disabled defaultChecked label={null} />
                    View Insights
                </label>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
                <label className={localClasses.checkboxWrapper}>
                    <Checkbox disabled defaultChecked label={null} />
                    Query for Insights
                </label>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
                <label className={localClasses.checkboxWrapper}>
                    <Checkbox disabled defaultChecked label={null} />
                    Export Insights
                </label>
            </Grid>

            {Array.from(permissionsMap).map(([permission, label]) => (
                <Grid item key={permission} xs={12} sm={6} lg={4}>
                    <label className={localClasses.checkboxWrapper}>
                        <Field
                            as={Checkbox}
                            type="checkbox"
                            name="permissions"
                            value={permission}
                            checked={props.selectedPermissions.includes(
                                permission
                            )}
                            disabled={adminChecked}
                            onChange={handlePermissionChange(permission)}
                        />
                        {label}
                    </label>
                </Grid>
            ))}

            {!initiallyAdmin.current &&
                props.selectedPermissions.includes(RPUserPermission.ADMIN) && (
                    <Grid item xs={12}>
                        <Alert severity="warning" sx={{ alignItems: 'center' }}>
                            As an administrator, this team member will be able
                            to manage subscriptions and payment methods.
                        </Alert>
                    </Grid>
                )}
        </Grid>
    )
}

const useLocalStyles = makeStyles()({
    adminLabel: {
        fontWeight: '500',
        fontSize: '1.125rem',
    },
    checkboxWrapper: {
        display: 'flex',
        alignItems: 'center',
        maxWidth: 'fit-content',
    },
})
